/**
 * Authentication Redux Slice
 * 
 * Manages authentication state including:
 * - User information
 * - JWT tokens
 * - Permissions
 * - Loading states
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AuthState, KeycloakTokenParsed, User } from '../types';
import { initKeycloak, keycloak, keycloakLogin, keycloakLogout, refreshKeycloakToken } from '../keycloak';
import { attachKeycloakTokenHandlers } from '../tokenManager';
import { env } from '@app/config/env';

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  user: null,
  token: null,
  refreshToken: null,
  idToken: null,
  tokenExpiry: null,
  permissions: [],
  error: null,
};

/**
 * Initialize authentication
 * This will be implemented with Keycloak
 */
export const initializeAuth = createAsyncThunk<
  {
    user: User;
    token: string;
    refreshToken: string;
    idToken: string;
    tokenExpiry: number;
    permissions: string[];
  } | null,
  void,
  { rejectValue: string }
>('auth/initialize', async (_, { rejectWithValue, dispatch }) => {
  try {
    const authenticated = await initKeycloak();

    // Attach refresh handlers once we have a dispatch reference
    attachKeycloakTokenHandlers({
      onTokenUpdate: (update) => dispatch(setToken(update)),
      onRefreshFailure: () => {
        dispatch(clearAuth());
        // After logout, return to the SPA root. The app will then redirect to Keycloak
        // via initKeycloak() with login-required.
        void keycloakLogout({
          redirectUri: `${window.location.origin}/`,
        });
      },
    });

    if (!authenticated) {
      return null;
    }

    const token = keycloak.token;
    const refreshToken = keycloak.refreshToken;
    // ID token is required for OIDC logout (id_token_hint parameter)
    const idToken = keycloak.idToken;

    if (!token || !refreshToken) {
      return rejectWithValue('Keycloak authenticated but tokens are missing');
    }

    // idToken may not always be present, but we should log if it's missing
    // as it's important for proper logout functionality
    if (!idToken) {
      console.warn('[auth] ID token is missing - logout may not work properly');
    }

    const parsed = (keycloak.tokenParsed ?? {}) as KeycloakTokenParsed;
    const tokenExpiry = typeof parsed.exp === 'number' ? parsed.exp * 1000 : Date.now() + 60_000;

    const permissionClaim = parsed.permission;
    const permissions: string[] = Array.isArray(permissionClaim)
      ? permissionClaim
      : typeof permissionClaim === 'string'
        ? [permissionClaim]
        : Array.isArray(parsed.permissions)
          ? parsed.permissions
          : [];

    const roleClaim = parsed.role;
    const rolesFromClaim: string[] = Array.isArray(roleClaim)
      ? roleClaim
      : typeof roleClaim === 'string'
        ? [roleClaim]
        : [];

    const rolesFromRealm: string[] = Array.isArray(parsed.realm_access?.roles)
      ? parsed.realm_access!.roles
      : [];

    const roles = Array.from(new Set([...rolesFromClaim, ...rolesFromRealm]));

    // Fetch ERP User ID from backend
    let erpUserId = '';
    try {
      const response = await fetch(`${env.apiUrl}/users/roles/userDetails`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json() as { userId: string; roles: string[]; permissions: string[] };
        erpUserId = data.userId;
        // Note: We could also use data.roles and data.permissions here,
        // but we're already getting them from the JWT token claims
      } else {
        console.warn('[auth] Failed to fetch ERP User ID from backend');
      }
    } catch (error) {
      console.error('[auth] Error fetching ERP User ID:', error);
    }

    const user: User = {
      id: parsed.sub ?? '',
      erpUserId,
      email: parsed.email ?? '',
      firstName: parsed.given_name ?? '',
      lastName: parsed.family_name ?? '',
      username: parsed.preferred_username ?? parsed.name ?? '',
      roles,
    };

    return { user, token, refreshToken, idToken: idToken ?? '', tokenExpiry, permissions };
  } catch {
    return rejectWithValue('Failed to initialize authentication');
  }
});

/**
 * Login with credentials
 */
export const login = createAsyncThunk(
  'auth/login',
  async (_, { rejectWithValue }) => {
    try {
      await keycloakLogin({ redirectUri: `${window.location.origin}/` });
      return;
    } catch {
      return rejectWithValue('Login failed');
    }
  }
);

/**
 * Logout
 *
 * Performs OIDC RP-Initiated Logout from Keycloak.
 * After logout, redirects back to the app origin where login-required
 * will trigger the Keycloak login page.
 */
export const logout = createAsyncThunk('auth/logout', async () => {
  // IMPORTANT: don't `await` here. Keycloak logout triggers a full-page redirect.
  // Awaiting can leave the thunk pending if the promise never resolves.
  void keycloakLogout({
    redirectUri: `${window.location.origin}/`,
  });
  return null;
});

/**
 * Refresh token
 */
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const result = await refreshKeycloakToken(30);

      if (!result.token || !result.refreshToken) {
        return rejectWithValue('Missing token(s) after refresh');
      }

      const tokenParsed = (result.tokenParsed ?? {}) as KeycloakTokenParsed;
      const tokenExpiry = typeof tokenParsed.exp === 'number' ? tokenParsed.exp * 1000 : Date.now() + 60_000;

      dispatch(
        setToken({
          token: result.token,
          refreshToken: result.refreshToken,
          tokenExpiry,
        })
      );

      return;
    } catch {
      return rejectWithValue('Token refresh failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set authentication data (called after successful Keycloak login)
     */
    setAuth: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        refreshToken: string;
        idToken: string;
        tokenExpiry: number;
        permissions: string[];
      }>
    ) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.idToken = action.payload.idToken;
      state.tokenExpiry = action.payload.tokenExpiry;
      state.permissions = action.payload.permissions;
      state.error = null;
    },

    /**
     * Update permissions (called when permissions change)
     */
    setPermissions: (state, action: PayloadAction<string[]>) => {
      state.permissions = action.payload;
    },

    /**
     * Update token (called after token refresh)
     */
    setToken: (
      state,
      action: PayloadAction<{
        token: string;
        refreshToken: string;
        tokenExpiry: number;
      }>
    ) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiry = action.payload.tokenExpiry;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Clear auth state (logout)
     */
    clearAuth: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.idToken = null;
      state.tokenExpiry = null;
      state.permissions = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Initialize auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isInitialized = true;
        state.isLoading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.idToken = action.payload.idToken;
          state.tokenExpiry = action.payload.tokenExpiry;
          state.permissions = action.payload.permissions;
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isInitialized = true;
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state) => {
        // Keycloak login usually redirects away from the SPA.
        // If it returns, just clear loading state.
        state.isLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.idToken = null;
      state.tokenExpiry = null;
      state.permissions = [];
      state.error = null;
    });

    // Refresh token
    builder
      .addCase(refreshToken.rejected, (state) => {
        // Token refresh failed - logout
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.idToken = null;
        state.tokenExpiry = null;
        state.permissions = [];
      });
  },
});

export const { setAuth, setPermissions, setToken, clearError, clearAuth } = authSlice.actions;
export default authSlice.reducer;
